import { interpolateBox } from './scenarios';

// There's no backend to burn detection boxes into a video server-side, so
// this re-plays the source clip off-screen, draws each frame onto a canvas
// together with the same scripted boxes shown live on the Dashboard, and
// records the canvas with MediaRecorder. It genuinely takes as long as the
// clip's real duration to run (frames have to be captured as they play),
// which is why the caller shows a progress toast rather than treating this
// as instant.
export function renderDetectedVideo({ src, incidents, classMeta, onProgress }) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    // Must be set before .src - sample clips now stream from the backend's
    // own origin (see videoUrl() in api.js), and this video gets drawn onto
    // a canvas every frame + captured via canvas.captureStream(), both of
    // which throw/produce a blank recording on a cross-origin source
    // unless the element is marked CORS-enabled. No-op for blob: URLs
    // (uploaded files), which aren't subject to CORS at all.
    video.crossOrigin = 'anonymous';
    video.src = src;
    video.muted = true;
    video.playsInline = true;
    video.style.position = 'fixed';
    video.style.left = '-9999px';
    video.style.top = '0';
    document.body.appendChild(video);

    const canvas = document.createElement('canvas');
    const chunks = [];
    let recorder;
    let rafId;
    let finished = false;
    let thumbnail = null;

    const cleanup = () => {
      if (rafId) cancelAnimationFrame(rafId);
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
    };

    const fail = (err) => {
      if (finished) return;
      finished = true;
      cleanup();
      reject(err instanceof Error ? err : new Error('Video rendering failed'));
    };

    video.onerror = () => fail(new Error('Could not load the source video for rendering.'));

    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');

      const mimeCandidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
      const mimeType = mimeCandidates.find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || 'video/webm';

      if (!window.MediaRecorder) {
        fail(new Error('This browser cannot record video (MediaRecorder unsupported).'));
        return;
      }

      try {
        const stream = canvas.captureStream(25);
        recorder = new MediaRecorder(stream, { mimeType });
      } catch (err) {
        fail(err);
        return;
      }

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      recorder.onerror = (event) => fail(event.error);
      recorder.onstop = () => {
        if (finished) return;
        finished = true;
        if (!thumbnail) {
          try { thumbnail = canvas.toDataURL('image/jpeg', 0.6); } catch { /* ignore */ }
        }
        cleanup();
        resolve({ blob: new Blob(chunks, { type: mimeType }), thumbnail });
      };

      const drawFrame = () => {
        if (video.paused || video.ended) return;
        const duration = video.duration || 1;
        const t = video.currentTime;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        let boxDrawn = false;

        incidents.forEach((incident) => {
          const startSec = incident.startFraction * duration;
          const endSec = incident.endFraction * duration;
          if (t < startSec || t > endSec) return;
          boxDrawn = true;

          const progress = Math.min(1, Math.max(0, (t - startSec) / Math.max(0.01, endSec - startSec)));
          const box = interpolateBox(incident.bbox, incident.bboxEnd, progress);
          if (!box) return;

          const liveConfidence = Math.round((incident.baseConfidence + (incident.maxConfidence - incident.baseConfidence) * progress) * 10) / 10;
          const meta = classMeta[incident.class_name] || { label: incident.class_name };
          const x = box.x * canvas.width;
          const y = box.y * canvas.height;
          const w = box.w * canvas.width;
          const h = box.h * canvas.height;

          const lineWidth = Math.max(3, canvas.width * 0.0018);
          ctx.lineWidth = lineWidth;
          ctx.strokeStyle = '#c0392b';
          ctx.strokeRect(x, y, w, h);

          const label = `${meta.label} ${Math.round(liveConfidence)}%`;
          const fontSize = Math.max(16, canvas.width * 0.014);
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          const padX = 6;
          const labelH = fontSize + padX * 1.4;
          const textWidth = ctx.measureText(label).width;
          ctx.fillStyle = '#c0392b';
          ctx.fillRect(x - lineWidth / 2, Math.max(0, y - labelH), textWidth + padX * 2, labelH);
          ctx.fillStyle = '#ffffff';
          ctx.textBaseline = 'bottom';
          ctx.fillText(label, x - lineWidth / 2 + padX, Math.max(labelH, y) - padX * 0.7);
        });

        // Grabs a thumbnail the first moment a box is actually on screen, so
        // the saved-videos grid shows the defect itself rather than an
        // arbitrary early frame that might predate it.
        if (!thumbnail && boxDrawn) {
          try { thumbnail = canvas.toDataURL('image/jpeg', 0.6); } catch { /* ignore */ }
        }

        if (onProgress && Number.isFinite(duration) && duration > 0) {
          onProgress(Math.min(100, Math.round((t / duration) * 100)));
        }

        rafId = requestAnimationFrame(drawFrame);
      };

      video.onended = () => {
        if (finished) return;
        setTimeout(() => {
          try { recorder.stop(); } catch { /* already stopped */ }
        }, 200);
      };

      video.play()
        .then(() => {
          recorder.start();
          rafId = requestAnimationFrame(drawFrame);
        })
        .catch(fail);
    };
  });
}
